import { Spacing } from '@persona-id/design-tokens';
import { ChangeEventValue, Coords } from 'google-map-react';
import { groupBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Map, { MapMarker } from 'dashboard/components/Map';
import { MapMarkerStyleType } from 'dashboard/components/Map/MapMarker';
import Pii from 'dashboard/components/Pii';
import { SimulatedDataSticker } from 'dashboard/components/SimulatedDataSticker/SimulatedDataSticker';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { EnvironmentType } from 'dashboard/models/environment';
import { LocationAccuracy } from 'dashboard/models/verification';
import { CustomLocation } from 'dashboard/modules/Location/model';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { paragraphMixins } from 'design-system/mixins/text';
import { COUNTRIES } from 'lib/constants/locales';
import { ABBR_DATE_FORMAT } from 'lib/constants/luxonFormats';
import useSet from 'lib/hooks/useSet';
import { ApiModel } from 'lib/interfaces/api';
import { Place, SerializedPlace } from 'lib/interfaces/place';
import { computeInitialMapCenterAndZoom, deserializePlace, serializePlace } from 'lib/utils/maps';
import { assertNever } from 'lib/utils/types';

const INQUIRY_DETAILS_MAP_HEIGHT = 300;

const ContainerCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const ContainerCardBody = styled('div')`
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${Spacing[4]};
  grid-auto-flow: row;
`;

const MapContainer = styled('div')`
  flex: none;
  height: ${INQUIRY_DETAILS_MAP_HEIGHT}px;
`;

const MarkerLink = styled.a`
  &:hover {
    cursor: pointer;
  }
`;

const TooltipListMatchLink = styled(Link)`
  ${paragraphMixins.md};
  color: ${({ theme }) => theme.color.variants.danger.default};
`;

type GeocodedEntity =
  | ApiModel.VerificationPhoneNumber
  | ApiModel.VerificationGovernmentId
  | ApiModel.VerificationDatabase
  | ApiModel.InquirySession
  | ApiModel.ReportProfile;

export interface LocationDetail extends MapMarker {
  id: string;
  type: GeocodedEntity;
  /**
   * Clickable location to display in the UI. May be an address or just a vague
   * location identifier like lat/lng.
   */
  location: string[];
  /**
   * Nonclickable location for country in the UI.
   */
  country: string;
  /**
   * Start date for expirable entities like government ID.
   */
  startDate?: string;
  /**
   * End date for expirable entities like government ID.
   */
  endDate?: string;
  /**
   * Number of list matches on a location
   */
  listMatchCount: number;
  /**
   * The level of approximation on the location.
   */
  locationAccuracy?: LocationAccuracy;
  /**
   * True if the geolocation does not match the entity's country code.
   */
  countryMismatch?: boolean;
}

interface LocationProps {
  isRedacted: boolean;
  locations: LocationDetail[];
  matchUrl?: string;
  environmentType: EnvironmentType;
  customLocations: CustomLocation[] | undefined;
}

/**
 * This will probably change to take an array of addresses and descriptors.
 * We will also uses these values to populate the bottom content.
 * Markers will be created by mapping addresses through places API.
 * Even better, backend can just supply coords along with each location.
 * We can also dedupe these locations if we don't actually care about making
 * pins unique if the same LatLng reoccurs across verifications.
 */
const Location = ({
  isRedacted,
  locations,
  matchUrl,
  environmentType,
  customLocations,
}: LocationProps) => {
  const [map, updateMap] = useState<google.maps.Map | undefined>();
  const customMapMarkers = useMemo(
    () => (customLocations ?? []).map(customLocationToMapMarker),
    [customLocations],
  );

  /**
   * Extract the highest and lowest lat/lng and generate NW and SE corners.
   * These are used as the defaults for map center and zoom to ensure as many
   * markers as possible are displayed by default.
   */
  const { center: initialCenter, zoom: initialZoom } = useMemo(() => {
    const combinedLocations: Place[] = [...locations, ...customMapMarkers];
    return computeInitialMapCenterAndZoom(INQUIRY_DETAILS_MAP_HEIGHT, combinedLocations);
  }, [locations, customMapMarkers]);

  /**
   * Key each of our locations by serialized coordinate to deduplicate.
   *
   * This map allows us to refer to all locations for a given marker. Mouse
   * interactions with a coordinate with multiple locations will "trigger" all
   * associated locations.
   */
  const locationCoordinateMap: Record<SerializedPlace, LocationDetail[]> = useMemo<
    Record<SerializedPlace, LocationDetail[]>
  >(
    () => groupBy<LocationDetail>(({ lat, lng }) => serializePlace({ lat, lng }), locations),
    [locations],
  );

  /**
   * Deduplicated list of markers.
   */
  const markers = useMemo<MapMarker[]>(() => {
    const customLocationMarkers: MapMarker[] = (customMapMarkers ?? []).map(
      (marker) => new MapMarker(marker),
    );

    if (isRedacted) return customLocationMarkers;

    const locationDetailMarkers: MapMarker[] = Object.entries<LocationDetail[]>(
      locationCoordinateMap,
    ).map(([serializedPlace, locations]) => {
      return new MapMarker({
        ...deserializePlace(serializedPlace as SerializedPlace),
        hasDot: locations.some((loc) => loc.listMatchCount > 0),
        // TODO: [PER-2780] Currently uncertain which style we want to display if we have two
        //       stacked on the same location. Default to use first one for now
        focusedMarkerColor: locations[0].focusedMarkerColor,
        defaultMarkerColor: locations[0].defaultMarkerColor,
        overlapCount: locations.length,
      });
    });

    return locationDetailMarkers.concat(customLocationMarkers);
  }, [isRedacted, locationCoordinateMap, customMapMarkers]);

  /**
   * Tracks when markers are "active", e.g., when they are hovered either in
   * the map or in the bottom info section.
   */
  const { add: activateMarker, delete: deactivateMarker, has: isMarkerActive } = useSet<string>();

  const [center, updateCenter] = useState<Coords>(initialCenter);
  const [zoom, updateZoom] = useState<number>(initialZoom);

  const isMapMarkerActive = useCallback(
    (place: Place) => isMarkerActive(serializePlace(place)),
    [isMarkerActive],
  );

  const renderMarkerTooltip = useCallback(
    (marker: MapMarker) => {
      // Typescript typing fails here because it cannot index into a hash with an Opaque type.
      // https://github.com/microsoft/TypeScript/issues/15746
      const locations: LocationDetail[] = (locationCoordinateMap as any)[serializePlace(marker)];

      // If marker is not in locationCoordinateMap, it means it is a customLocations marker.
      if (
        locations == null &&
        marker.lat != null &&
        marker.lng != null &&
        marker.radiusMeters != null
      ) {
        return (
          <>
            {marker.primaryTooltipLabel && <p>{marker.primaryTooltipLabel}</p>}
            <p>{`${stringifyLatitude(marker.lat)}, ${stringifyLongitude(
              marker.lng,
            )} (${formatNumber(marker.radiusMeters)}m)`}</p>
          </>
        );
      }

      return locations.map(({ primaryTooltipLabel, secondaryTooltipLabel }, i) => (
        <Fragment key={i}>
          {primaryTooltipLabel && <p>{primaryTooltipLabel}</p>}
          {secondaryTooltipLabel &&
            (matchUrl ? (
              <TooltipListMatchLink to={`${matchUrl}/lists`}>
                {secondaryTooltipLabel}
              </TooltipListMatchLink>
            ) : (
              <p>{secondaryTooltipLabel}</p>
            ))}
        </Fragment>
      ));
    },
    [locationCoordinateMap, matchUrl],
  );

  const handleMapChange = useCallback(({ center: { lat, lng }, zoom }: ChangeEventValue) => {
    updateCenter({ lat, lng });
    updateZoom(zoom);
  }, []);

  /**
   * Centers the map on the clicked marker.
   * In the future this can somehow call out the text of the clicked location
   * but for now let's just center the map.
   */
  const handleMarkerClick = useCallback(
    (_markerIdx: number, { lat, lng }: Coords) => updateCenter({ lat, lng }),
    [],
  );

  /**
   * Centers the map on the marker linked to the clicked anchor tag.
   */
  const handleLocationDetailClick = useCallback(
    (locationDetail: LocationDetail) =>
      updateCenter({ lat: locationDetail.lat, lng: locationDetail.lng }),
    [],
  );

  const handleLocationDetailMouseOver = useCallback(
    (locationDetail: LocationDetail) => activateMarker(serializePlace(locationDetail)),
    [],
  );

  const handleLocationDetailMouseOut = useCallback(
    (locationDetail: LocationDetail) => deactivateMarker(serializePlace(locationDetail)),
    [],
  );

  return (
    <ContainerCard>
      <Card.Body>
        <Card.Title>
          Location
          {environmentType === ApiModel.EnvironmentSandbox && (
            <SimulatedDataSticker dataSectionLabel='location' />
          )}
        </Card.Title>
        <MapContainer>
          <Map
            // For circle-drawing
            map={map}
            handleGoogleApiLoaded={updateMap}
            markers={markers}
            center={center}
            zoom={zoom}
            onChange={handleMapChange}
            onMarkerClick={handleMarkerClick}
            isMarkerActive={isMapMarkerActive}
            renderTooltip={renderMarkerTooltip}
          />
        </MapContainer>
        <Pii isRedacted={isRedacted} hideOnRedacted={true}>
          <ContainerCardBody>
            {locations.length > 0
              ? locations.map((l) => (
                  <LocationDetailDisplay
                    key={l.id}
                    onClick={handleLocationDetailClick}
                    onMouseOver={handleLocationDetailMouseOver}
                    onMouseOut={handleLocationDetailMouseOut}
                    {...l}
                  />
                ))
              : 'No locations are available for this inquiry.'}
          </ContainerCardBody>
        </Pii>
      </Card.Body>
    </ContainerCard>
  );
};

const LocationDetailContainer = styled('div')`
  display: flex;
  flex-direction: column;
  > *:first-child {
    margin-bottom: ${Spacing[1]};
  }

  white-space: pre;
`;

const MatchMarkerContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  > * {
    margin-bottom: ${Spacing[2]};
    margin-right: ${Spacing[2]};
  }
`;

const SubtitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/**
 * Props for activating location detail marker.
 */
interface LocationDetailDisplayProps extends LocationDetail {
  onClick: (locationDetail: LocationDetail) => void;
  onMouseOver: (locationDetail: LocationDetail) => void;
  onMouseOut: (locationDetail: LocationDetail) => void;
}

const LocationDetailDisplay = ({
  onClick,
  onMouseOver,
  onMouseOut,
  ...locationDetail
}: LocationDetailDisplayProps) => {
  const {
    location,
    country,
    startDate,
    endDate,
    listMatchCount,
    locationAccuracy,
    countryMismatch,
    primaryTooltipLabel,
  } = locationDetail;
  return (
    <LocationDetailContainer>
      <SubtitleContainer>
        <Card.Subtitle>{primaryTooltipLabel}</Card.Subtitle>
      </SubtitleContainer>
      <MatchMarkerContainer>
        {listMatchCount > 0 && <Sticker variant='danger'>List Matched</Sticker>}
        {countryMismatch && <Sticker variant='danger'>Country Mismatch</Sticker>}
        {locationAccuracy != null && (
          <Sticker
            variant={
              [LocationAccuracy.RoofTop, LocationAccuracy.RangeInterpolated].includes(
                locationAccuracy,
              )
                ? 'basic'
                : 'danger'
            }
          >
            {translateLocationAccuracy(locationAccuracy)}
          </Sticker>
        )}
      </MatchMarkerContainer>
      <MarkerLink
        onMouseOver={() => onMouseOver(locationDetail)}
        onMouseOut={() => onMouseOut(locationDetail)}
        onClick={() => onClick(locationDetail)}
      >
        {location.filter(Boolean).length > 0 ? location.join('\n') : 'Unknown'}
      </MarkerLink>
      <span>{COUNTRIES[country as keyof typeof COUNTRIES]}</span>
      <LocationDetailDates startDate={startDate} endDate={endDate} />
    </LocationDetailContainer>
  );
};

function translateLocationAccuracy(locationType: LocationAccuracy) {
  switch (locationType) {
    case LocationAccuracy.RoofTop:
      return 'Precise Location';
    case LocationAccuracy.RangeInterpolated:
      return 'Interpolated Location';
    case LocationAccuracy.GeometricCenter:
      return 'Centered Location';
    case LocationAccuracy.Approximate:
      return 'Approximated Location';
    default:
      assertNever(locationType);
  }
}

function customLocationToMapMarker(location: CustomLocation): MapMarker {
  return {
    lat: location.latitude,
    lng: location.longitude,
    radiusMeters: location['radius-meters'],
    hasDot: false,
    styleType: MapMarkerStyleType.CircleOnly,
    primaryTooltipLabel: location.name,
  };
}

/**
 * While it shouldn't be possible for `endDate` to exist while `startDate` does
 * not, it can happen if there is an error in parsing. This could potentially
 * be a signal to surface (though it's an error on our end).
 */
const LocationDetailDates = ({
  startDate,
  endDate,
}: Pick<LocationDetail, 'startDate' | 'endDate'>) => {
  return startDate == null && endDate == null ? null : (
    <em>
      {`(${
        startDate == null ? 'Unknown' : DateTime.fromISO(startDate).toFormat(ABBR_DATE_FORMAT)
      } - ${endDate == null ? 'Current' : DateTime.fromISO(endDate).toFormat(ABBR_DATE_FORMAT)})`}
    </em>
  );
};

export default Location;
